import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StabilizerCode,
  EditorState,
  EditorTool,
  StabilizerCircuit,
  createEmptyCode,
  createDefaultEditorState,
  buildDefaultCircuit,
  DEFAULT_X_COLOR,
  DEFAULT_Z_COLOR,
} from '../types/stabilizer';
import StabilizerCanvas from '../components/stabilizer/StabilizerCanvas';
import StabilizerToolbar from '../components/stabilizer/StabilizerToolbar';
import AnalysisPanel from '../components/stabilizer/AnalysisPanel';
import CircuitComposer from '../components/stabilizer/CircuitComposer';

const ToricCanvas3D = lazy(() => import('../components/toric/ToricCanvas3D'));

// ── Preset templates ──

function makeDistance3Surface(): StabilizerCode {
  const G = 40;
  const qubits = [
    // 9 data qubits in a 3x3 grid
    { id: 'q0', index: 0, x: 1 * G, y: 1 * G },
    { id: 'q1', index: 1, x: 2 * G, y: 1 * G },
    { id: 'q2', index: 2, x: 3 * G, y: 1 * G },
    { id: 'q3', index: 3, x: 1 * G, y: 2 * G },
    { id: 'q4', index: 4, x: 2 * G, y: 2 * G },
    { id: 'q5', index: 5, x: 3 * G, y: 2 * G },
    { id: 'q6', index: 6, x: 1 * G, y: 3 * G },
    { id: 'q7', index: 7, x: 2 * G, y: 3 * G },
    { id: 'q8', index: 8, x: 3 * G, y: 3 * G },
  ];

  const edges = [
    // Horizontal
    { id: 'e01', qubitIds: ['q0', 'q1'] as [string, string] },
    { id: 'e12', qubitIds: ['q1', 'q2'] as [string, string] },
    { id: 'e34', qubitIds: ['q3', 'q4'] as [string, string] },
    { id: 'e45', qubitIds: ['q4', 'q5'] as [string, string] },
    { id: 'e67', qubitIds: ['q6', 'q7'] as [string, string] },
    { id: 'e78', qubitIds: ['q7', 'q8'] as [string, string] },
    // Vertical
    { id: 'e03', qubitIds: ['q0', 'q3'] as [string, string] },
    { id: 'e14', qubitIds: ['q1', 'q4'] as [string, string] },
    { id: 'e25', qubitIds: ['q2', 'q5'] as [string, string] },
    { id: 'e36', qubitIds: ['q3', 'q6'] as [string, string] },
    { id: 'e47', qubitIds: ['q4', 'q7'] as [string, string] },
    { id: 'e58', qubitIds: ['q5', 'q8'] as [string, string] },
  ];

  // Rotated surface code d=3 stabilizers (standard layout)
  //   X(top): q0,q1  |  Z(UL): q0,q1,q3,q4  |  X(UR): q1,q2,q4,q5
  //   Z(right): q2,q5 |  Z(left): q3,q6
  //   X(LL): q3,q4,q6,q7  |  Z(LR): q4,q5,q7,q8  |  X(bot): q7,q8
  const stabilizers = [
    // X stabilizers
    { id: 'sx0', type: 'X' as const, qubitIds: ['q0', 'q1'], color: DEFAULT_X_COLOR, name: 'X-top' },
    { id: 'sx1', type: 'X' as const, qubitIds: ['q1', 'q2', 'q4', 'q5'], color: DEFAULT_X_COLOR, name: 'X-UR' },
    { id: 'sx2', type: 'X' as const, qubitIds: ['q3', 'q4', 'q6', 'q7'], color: DEFAULT_X_COLOR, name: 'X-LL' },
    { id: 'sx3', type: 'X' as const, qubitIds: ['q7', 'q8'], color: DEFAULT_X_COLOR, name: 'X-bot' },
    // Z stabilizers
    { id: 'sz0', type: 'Z' as const, qubitIds: ['q0', 'q1', 'q3', 'q4'], color: DEFAULT_Z_COLOR, name: 'Z-UL' },
    { id: 'sz1', type: 'Z' as const, qubitIds: ['q2', 'q5'], color: DEFAULT_Z_COLOR, name: 'Z-right' },
    { id: 'sz2', type: 'Z' as const, qubitIds: ['q3', 'q6'], color: DEFAULT_Z_COLOR, name: 'Z-left' },
    { id: 'sz3', type: 'Z' as const, qubitIds: ['q4', 'q5', 'q7', 'q8'], color: DEFAULT_Z_COLOR, name: 'Z-LR' },
  ];

  return { name: 'Surface Code d=3', qubits, edges, stabilizers, nextQubitIndex: 9 };
}

function makeColorCode(): StabilizerCode {
  const G = 50;
  // 7-qubit color code (Steane code) layout
  const qubits = [
    { id: 'q0', index: 0, x: 3 * G, y: 1 * G },
    { id: 'q1', index: 1, x: 2 * G, y: 2 * G },
    { id: 'q2', index: 2, x: 4 * G, y: 2 * G },
    { id: 'q3', index: 3, x: 1 * G, y: 3 * G },
    { id: 'q4', index: 4, x: 3 * G, y: 3 * G },
    { id: 'q5', index: 5, x: 5 * G, y: 3 * G },
    { id: 'q6', index: 6, x: 2 * G, y: 4 * G },
    // Extra boundary qubits not needed for Steane - this is just the 7 data qubits
  ];

  const edges = [
    { id: 'e01', qubitIds: ['q0', 'q1'] as [string, string] },
    { id: 'e02', qubitIds: ['q0', 'q2'] as [string, string] },
    { id: 'e13', qubitIds: ['q1', 'q3'] as [string, string] },
    { id: 'e14', qubitIds: ['q1', 'q4'] as [string, string] },
    { id: 'e24', qubitIds: ['q2', 'q4'] as [string, string] },
    { id: 'e25', qubitIds: ['q2', 'q5'] as [string, string] },
    { id: 'e36', qubitIds: ['q3', 'q6'] as [string, string] },
    { id: 'e46', qubitIds: ['q4', 'q6'] as [string, string] },
  ];

  const stabilizers = [
    { id: 'sx0', type: 'X' as const, qubitIds: ['q0', 'q1', 'q2', 'q4'], color: '#ef4444', name: 'X-red' },
    { id: 'sx1', type: 'X' as const, qubitIds: ['q1', 'q3', 'q4', 'q6'], color: '#22c55e', name: 'X-green' },
    { id: 'sx2', type: 'X' as const, qubitIds: ['q2', 'q4', 'q5', 'q6'], color: '#3b82f6', name: 'X-blue' },
    { id: 'sz0', type: 'Z' as const, qubitIds: ['q0', 'q1', 'q2', 'q4'], color: '#ef4444', name: 'Z-red' },
    { id: 'sz1', type: 'Z' as const, qubitIds: ['q1', 'q3', 'q4', 'q6'], color: '#22c55e', name: 'Z-green' },
    { id: 'sz2', type: 'Z' as const, qubitIds: ['q2', 'q4', 'q5', 'q6'], color: '#3b82f6', name: 'Z-blue' },
  ];

  return { name: 'Color Code [[7,1,3]]', qubits, edges, stabilizers, nextQubitIndex: 7 };
}

function make3QubitBitFlip(): StabilizerCode {
  const G = 60;
  // 3 data qubits in a line: q0 — q1 — q2
  const qubits = [
    { id: 'q0', index: 0, x: 1 * G, y: 2 * G },
    { id: 'q1', index: 1, x: 2 * G, y: 2 * G },
    { id: 'q2', index: 2, x: 3 * G, y: 2 * G },
  ];

  const edges = [
    { id: 'e01', qubitIds: ['q0', 'q1'] as [string, string] },
    { id: 'e12', qubitIds: ['q1', 'q2'] as [string, string] },
  ];

  // Two Z stabilizers: Z0Z1 and Z1Z2
  const stabilizers = [
    { id: 'sz0', type: 'Z' as const, qubitIds: ['q0', 'q1'], color: DEFAULT_Z_COLOR, name: 'Z₀Z₁' },
    { id: 'sz1', type: 'Z' as const, qubitIds: ['q1', 'q2'], color: '#0891b2', name: 'Z₁Z₂' },
  ];

  return { name: '3-Qubit Bit-Flip Code', qubits, edges, stabilizers, nextQubitIndex: 3 };
}

function make3QubitPhaseFlip(): StabilizerCode {
  const G = 60;
  // 3 data qubits in a line: q0 — q1 — q2
  const qubits = [
    { id: 'q0', index: 0, x: 1 * G, y: 2 * G },
    { id: 'q1', index: 1, x: 2 * G, y: 2 * G },
    { id: 'q2', index: 2, x: 3 * G, y: 2 * G },
  ];

  const edges = [
    { id: 'e01', qubitIds: ['q0', 'q1'] as [string, string] },
    { id: 'e12', qubitIds: ['q1', 'q2'] as [string, string] },
  ];

  // Two X stabilizers: X0X1 and X1X2
  const stabilizers = [
    { id: 'sx0', type: 'X' as const, qubitIds: ['q0', 'q1'], color: DEFAULT_X_COLOR, name: 'X₀X₁' },
    { id: 'sx1', type: 'X' as const, qubitIds: ['q1', 'q2'], color: '#ec4899', name: 'X₁X₂' },
  ];

  return { name: '3-Qubit Phase-Flip Code', qubits, edges, stabilizers, nextQubitIndex: 3 };
}

function makeToricCode(): StabilizerCode {
  const G = 50;
  // Simplified toric code on 4x4 grid (8 data qubits, periodic)
  const qubits = [
    { id: 'q0', index: 0, x: 2 * G, y: 1 * G },
    { id: 'q1', index: 1, x: 4 * G, y: 1 * G },
    { id: 'q2', index: 2, x: 1 * G, y: 2 * G },
    { id: 'q3', index: 3, x: 3 * G, y: 2 * G },
    { id: 'q4', index: 4, x: 2 * G, y: 3 * G },
    { id: 'q5', index: 5, x: 4 * G, y: 3 * G },
    { id: 'q6', index: 6, x: 1 * G, y: 4 * G },
    { id: 'q7', index: 7, x: 3 * G, y: 4 * G },
  ];

  const edges = [
    { id: 'e02', qubitIds: ['q0', 'q2'] as [string, string] },
    { id: 'e03', qubitIds: ['q0', 'q3'] as [string, string] },
    { id: 'e13', qubitIds: ['q1', 'q3'] as [string, string] },
    { id: 'e24', qubitIds: ['q2', 'q4'] as [string, string] },
    { id: 'e34', qubitIds: ['q3', 'q4'] as [string, string] },
    { id: 'e35', qubitIds: ['q3', 'q5'] as [string, string] },
    { id: 'e46', qubitIds: ['q4', 'q6'] as [string, string] },
    { id: 'e47', qubitIds: ['q4', 'q7'] as [string, string] },
    { id: 'e57', qubitIds: ['q5', 'q7'] as [string, string] },
  ];

  const stabilizers = [
    { id: 'sv0', type: 'Z' as const, qubitIds: ['q0', 'q2', 'q3'], color: DEFAULT_Z_COLOR, name: 'Z-vertex-0' },
    { id: 'sv1', type: 'Z' as const, qubitIds: ['q3', 'q4', 'q5'], color: DEFAULT_Z_COLOR, name: 'Z-vertex-1' },
    { id: 'sf0', type: 'X' as const, qubitIds: ['q0', 'q1', 'q3'], color: DEFAULT_X_COLOR, name: 'X-face-0' },
    { id: 'sf1', type: 'X' as const, qubitIds: ['q2', 'q4', 'q6'], color: DEFAULT_X_COLOR, name: 'X-face-1' },
    { id: 'sf2', type: 'X' as const, qubitIds: ['q4', 'q5', 'q7'], color: DEFAULT_X_COLOR, name: 'X-face-2' },
  ];

  return { name: 'Toric Code (partial)', qubits, edges, stabilizers, nextQubitIndex: 8 };
}

/**
 * Generate a standard L×L toric code as a StabilizerCode with qubit positions
 * in parametric (x=phi, y=theta) coordinates for the 3D torus editor.
 */
function makeToricCode3D(L = 3): StabilizerCode {
  const mod = (v: number) => ((v % L) + L) % L;
  const vertexAngle = (row: number, col: number) => ({
    theta: (row / L) * 2 * Math.PI,
    phi: (col / L) * 2 * Math.PI,
  });

  const qubits: { id: string; index: number; x: number; y: number }[] = [];
  const edges: { id: string; qubitIds: [string, string] }[] = [];
  let idx = 0;

  // Horizontal edges: midpoint between (row,col) and (row, col+1)
  for (let row = 0; row < L; row++) {
    for (let col = 0; col < L; col++) {
      const a = vertexAngle(row, col);
      const b = vertexAngle(row, mod(col + 1));
      let midPhi = (a.phi + b.phi) / 2;
      if (Math.abs(b.phi - a.phi) > Math.PI) {
        midPhi = (a.phi + b.phi + 2 * Math.PI) / 2;
        if (midPhi > 2 * Math.PI) midPhi -= 2 * Math.PI;
      }
      qubits.push({
        id: `h-${row}-${col}`,
        index: idx++,
        x: midPhi,  // phi
        y: (a.theta + b.theta) / 2, // theta
      });
    }
  }

  // Vertical edges: midpoint between (row,col) and (row+1, col)
  for (let row = 0; row < L; row++) {
    for (let col = 0; col < L; col++) {
      const a = vertexAngle(row, col);
      const b = vertexAngle(mod(row + 1), col);
      let midTheta = (a.theta + b.theta) / 2;
      if (Math.abs(b.theta - a.theta) > Math.PI) {
        midTheta = (a.theta + b.theta + 2 * Math.PI) / 2;
        if (midTheta > 2 * Math.PI) midTheta -= 2 * Math.PI;
      }
      qubits.push({
        id: `v-${row}-${col}`,
        index: idx++,
        x: (a.phi + b.phi) / 2, // phi
        y: midTheta, // theta
      });
    }
  }

  // Edges between qubits sharing a stabilizer (for visualization)
  // Connect each horizontal edge to its neighboring vertical edges
  for (let row = 0; row < L; row++) {
    for (let col = 0; col < L; col++) {
      // Face edges: h(row,col) <-> v(row,col), h(row,col) <-> v(row,col+1)
      edges.push({ id: `e-h${row}${col}-v${row}${col}`, qubitIds: [`h-${row}-${col}`, `v-${row}-${col}`] });
      edges.push({ id: `e-h${row}${col}-v${row}${mod(col + 1)}`, qubitIds: [`h-${row}-${col}`, `v-${row}-${mod(col + 1)}`] });
    }
  }

  // X-stabilizers (faces/plaquettes)
  const stabilizers: { id: string; type: 'X' | 'Z'; qubitIds: string[]; color: string; name: string }[] = [];
  for (let row = 0; row < L; row++) {
    for (let col = 0; col < L; col++) {
      stabilizers.push({
        id: `Xf-${row}-${col}`,
        type: 'X',
        qubitIds: [
          `h-${row}-${col}`,
          `h-${mod(row + 1)}-${col}`,
          `v-${row}-${col}`,
          `v-${row}-${mod(col + 1)}`,
        ],
        color: DEFAULT_X_COLOR,
        name: `X(${row},${col})`,
      });
    }
  }

  // Z-stabilizers (vertices/stars)
  for (let row = 0; row < L; row++) {
    for (let col = 0; col < L; col++) {
      stabilizers.push({
        id: `Zv-${row}-${col}`,
        type: 'Z',
        qubitIds: [
          `h-${row}-${col}`,
          `h-${row}-${mod(col - 1)}`,
          `v-${row}-${col}`,
          `v-${mod(row - 1)}-${col}`,
        ],
        color: DEFAULT_Z_COLOR,
        name: `Z(${row},${col})`,
      });
    }
  }

  return {
    name: `Toric Code L=${L}`,
    qubits,
    edges,
    stabilizers,
    nextQubitIndex: idx,
  };
}

// ── Serialization ──

function exportCode(code: StabilizerCode): string {
  return JSON.stringify(code, null, 2);
}

function importCode(json: string): StabilizerCode | null {
  try {
    const parsed = JSON.parse(json);
    if (parsed.qubits && parsed.stabilizers) return parsed as StabilizerCode;
  } catch {}
  return null;
}

// ── Page component ──

export default function StabilizerCodeEditorPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState<StabilizerCode>(() => {
    const saved = localStorage.getItem('stabilizerCode');
    if (saved) {
      const parsed = importCode(saved);
      if (parsed) return parsed;
    }
    return createEmptyCode();
  });
  const [editorState, setEditorState] = useState<EditorState>(createDefaultEditorState);
  const [showExport, setShowExport] = useState(false);
  const [importText, setImportText] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(true);
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');
  const [toricR, setToricR] = useState(3);
  const [toricr, setToricr] = useState(1.2);
  // Separate code storage for 2D and 3D so they don't contaminate each other
  const savedCode2D = useRef<StabilizerCode | null>(null);
  const savedCode3D = useRef<StabilizerCode>(createEmptyCode());
  const handleViewModeChange = useCallback((mode: '2d' | '3d') => {
    if (mode === viewMode) return;
    if (mode === '3d') {
      // Save current 2D code and load the 3D code
      savedCode2D.current = code;
      setCode(savedCode3D.current);
    } else {
      // Save current 3D code and restore the 2D code
      savedCode3D.current = code;
      if (savedCode2D.current) setCode(savedCode2D.current);
    }
    setViewMode(mode);
    // Reset editor state
    setEditorState(createDefaultEditorState());
  }, [viewMode, code]);

  const [editingCircuitStabId, setEditingCircuitStabId] = useState<string | null>(null);
  const [leftWidth, setLeftWidth] = useState(272);
  const [rightWidth, setRightWidth] = useState(384);
  const [circuitFlippedIds, setCircuitFlippedIds] = useState<Set<string>>(new Set());
  const [tannerFlippedIds, setTannerFlippedIds] = useState<Set<string>>(new Set());
  const flippedStabilizerIds = useMemo(() => {
    const merged = new Set(circuitFlippedIds);
    for (const id of tannerFlippedIds) merged.add(id);
    return merged;
  }, [circuitFlippedIds, tannerFlippedIds]);
  const draggingPanel = useRef<'left' | 'right' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-save
  useEffect(() => {
    localStorage.setItem('stabilizerCode', exportCode(code));
  }, [code]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      const toolMap: Record<string, EditorTool> = { v: 'select', q: 'addQubit', e: 'addEdge', s: 'addStabilizer' };
      const tool = toolMap[e.key.toLowerCase()];
      if (tool) {
        setEditorState((prev) => ({
          ...prev,
          tool,
          edgeStartQubitId: null,
          selectedQubitIds: tool === 'addStabilizer' ? prev.selectedQubitIds : new Set(),
        }));
      }
      // Escape: deselect
      if (e.key === 'Escape') {
        setEditorState((prev) => ({
          ...prev,
          selectedQubitIds: new Set(),
          selectedStabilizerId: null,
          edgeStartQubitId: null,
        }));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Panel resize handlers
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingPanel.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (draggingPanel.current === 'left') {
        const newW = Math.max(200, Math.min(rect.width - rightWidth - 100, e.clientX - rect.left));
        setLeftWidth(newW);
      } else {
        const newW = Math.max(200, Math.min(rect.width - leftWidth - 100, rect.right - e.clientX));
        setRightWidth(newW);
      }
    };
    const onMouseUp = () => {
      draggingPanel.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResizeLeft = useCallback(() => {
    draggingPanel.current = 'left';
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const startResizeRight = useCallback(() => {
    draggingPanel.current = 'right';
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleImport = useCallback(() => {
    const parsed = importCode(importText);
    if (parsed) {
      setCode(parsed);
      setImportText('');
      setShowExport(false);
    }
  }, [importText]);

  // Circuit composer helpers
  const editingStab = editingCircuitStabId ? code.stabilizers.find((s) => s.id === editingCircuitStabId) : null;

  const getEditingCircuit = useCallback((): StabilizerCircuit | null => {
    if (!editingStab) return null;
    if (editingStab.circuit) return editingStab.circuit;
    // Build default circuit
    const qubitIndexMap = new Map(code.qubits.map((q) => [q.id, q.index]));
    const dataIndices = editingStab.qubitIds
      .map((id) => qubitIndexMap.get(id) ?? -1)
      .filter((x) => x >= 0)
      .sort((a, b) => a - b);
    const ancillaIdx = code.qubits.length;
    return buildDefaultCircuit(editingStab.type, dataIndices, ancillaIdx, editingStab.id);
  }, [editingStab, code.qubits]);

  const handleCircuitChange = useCallback((newCircuit: StabilizerCircuit) => {
    if (!editingCircuitStabId) return;
    setCode((prev) => ({
      ...prev,
      stabilizers: prev.stabilizers.map((s) =>
        s.id === editingCircuitStabId ? { ...s, circuit: newCircuit } : s
      ),
    }));
  }, [editingCircuitStabId]);

  const handleCloseComposer = useCallback(() => {
    setEditingCircuitStabId(null);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            &larr; Back
          </button>
          <h1 className="text-lg font-semibold">
            Stabilizer Code Editor
          </h1>
          <span className="text-sm text-gray-500">{code.name}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* 2D / 3D toggle */}
          <div className="flex bg-gray-800 rounded overflow-hidden">
            <button
              onClick={() => handleViewModeChange('2d')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === '2d' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              2D
            </button>
            <button
              onClick={() => handleViewModeChange('3d')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === '3d' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              3D Toric
            </button>
          </div>

          {/* Presets */}
          <div className="relative group">
            <button className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 rounded transition-colors">
              Templates
            </button>
            <div className="absolute right-0 top-full mt-1 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl hidden group-hover:block z-40">
              <button
                onClick={() => setCode(createEmptyCode())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700 rounded-t-lg"
              >
                Empty canvas
              </button>
              <button
                onClick={() => setCode(make3QubitBitFlip())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700"
              >
                3-qubit bit-flip code
              </button>
              <button
                onClick={() => setCode(make3QubitPhaseFlip())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700"
              >
                3-qubit phase-flip code
              </button>
              <button
                onClick={() => setCode(makeDistance3Surface())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700"
              >
                Surface code d=3
              </button>
              <button
                onClick={() => setCode(makeColorCode())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700"
              >
                Color code [[7,1,3]]
              </button>
              <button
                onClick={() => setCode(makeToricCode())}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700"
              >
                Toric code (2D partial)
              </button>
              <button
                onClick={() => {
                  const tc = makeToricCode3D(3);
                  setCode(tc);
                  savedCode3D.current = tc;
                  if (viewMode !== '3d') {
                    savedCode2D.current = code;
                    setViewMode('3d');
                    setEditorState(createDefaultEditorState());
                  }
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700 rounded-b-lg"
              >
                Toric code L=3 (3D torus)
              </button>
            </div>
          </div>

          {/* Analysis toggle */}
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              showAnalysis ? 'bg-indigo-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
            }`}
          >
            Analysis
          </button>

          {/* Export/Import */}
          <button
            onClick={() => setShowExport(!showExport)}
            className="px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 rounded transition-colors"
          >
            {showExport ? 'Close' : 'Export / Import'}
          </button>
        </div>
      </header>

      {/* Export/Import modal */}
      {showExport && (
        <div className="absolute top-12 right-4 z-50 w-96 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-4">
          <h3 className="text-sm font-semibold mb-2">Export (JSON)</h3>
          <textarea
            readOnly
            value={exportCode(code)}
            className="w-full h-32 bg-gray-800 text-xs font-mono text-gray-300 p-2 rounded border border-gray-700 mb-3"
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          />
          <h3 className="text-sm font-semibold mb-2">Import</h3>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="Paste JSON here..."
            className="w-full h-24 bg-gray-800 text-xs font-mono text-gray-300 p-2 rounded border border-gray-700 mb-2 placeholder-gray-600"
          />
          <button
            onClick={handleImport}
            disabled={!importText.trim()}
            className="w-full py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-sm font-medium transition-colors"
          >
            Import
          </button>
        </div>
      )}

      {/* Main layout */}
      <div ref={containerRef} className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 48px)' }}>
        {/* Left panel */}
        <div className="shrink-0 overflow-hidden" style={{ width: leftWidth }}>
          <StabilizerToolbar
            code={code}
            editorState={editorState}
            onCodeChange={setCode}
            onEditorStateChange={setEditorState}
            onEditCircuit={setEditingCircuitStabId}
            onFlippedStabilizersChange={setTannerFlippedIds}
          />
        </div>
        {/* Left resize handle */}
        <div
          onMouseDown={startResizeLeft}
          className="w-1 shrink-0 bg-gray-800 hover:bg-indigo-500 cursor-col-resize transition-colors"
        />
        {/* Center area */}
        {editingCircuitStabId && editingStab && getEditingCircuit() ? (
          <div className="flex-1 min-w-0">
            <CircuitComposer
              circuit={getEditingCircuit()!}
              stabName={editingStab.name || `${editingStab.type}-Stab-${editingStab.id.slice(-4)}`}
              onChange={handleCircuitChange}
              onClose={handleCloseComposer}
            />
          </div>
        ) : viewMode === '3d' ? (
          <div className="flex-1 min-w-0 relative">
            <Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-950 text-gray-500">Loading 3D...</div>}>
              <ToricCanvas3D
                code={code}
                editorState={editorState}
                R={toricR}
                r={toricr}
                onCodeChange={setCode}
                onEditorStateChange={setEditorState}
              />
            </Suspense>
            {/* Torus shape controls overlay */}
            <div className="absolute top-3 right-3 flex flex-col gap-1.5 bg-gray-900/90 backdrop-blur-sm rounded-lg border border-gray-700 p-2 text-xs z-10">
              <div className="text-gray-400 font-semibold uppercase tracking-wider mb-1">Torus Shape</div>
              <label className="flex items-center justify-between gap-3 text-gray-300">
                R
                <div className="flex items-center gap-1.5">
                  <input type="range" min={2} max={5} step={0.1} value={toricR} onChange={(e) => setToricR(Number(e.target.value))} className="w-20 accent-indigo-500" />
                  <span className="font-mono w-4 text-right">{toricR.toFixed(1)}</span>
                </div>
              </label>
              <label className="flex items-center justify-between gap-3 text-gray-300">
                r
                <div className="flex items-center gap-1.5">
                  <input type="range" min={0.4} max={2} step={0.1} value={toricr} onChange={(e) => setToricr(Number(e.target.value))} className="w-20 accent-indigo-500" />
                  <span className="font-mono w-4 text-right">{toricr.toFixed(1)}</span>
                </div>
              </label>
              <hr className="border-gray-700 my-1" />
              <div className="text-gray-500 font-mono">
                {code.qubits.length} qubits · {code.stabilizers.length} stabs
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            <StabilizerCanvas
              code={code}
              editorState={editorState}
              onCodeChange={setCode}
              onEditorStateChange={setEditorState}
              flippedStabilizerIds={flippedStabilizerIds}
            />
          </div>
        )}
        {/* Right panel */}
        {showAnalysis && (
          <>
            {/* Right resize handle */}
            <div
              onMouseDown={startResizeRight}
              className="w-1 shrink-0 bg-gray-800 hover:bg-indigo-500 cursor-col-resize transition-colors"
            />
            <div className="shrink-0 overflow-hidden" style={{ width: rightWidth }}>
              <AnalysisPanel code={code} onCodeChange={setCode} onFlippedStabilizersChange={setCircuitFlippedIds} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

